import React, { useState } from 'react'
import BoardItem from '@cloudscape-design/board-components/board-item'
import {
  Header,
  Button,
  ButtonDropdown,
  Box,
  Alert,
  Spinner,
  StatusIndicator,
  Modal,
  SpaceBetween,
  Container
} from '@cloudscape-design/components'
import { DashboardWidget } from '../types'
import { ChartWidget } from './widgets/ChartWidget/ChartWidget'
import { ChartWidgetConfigAdvanced as ChartConfigDialog } from './widgets/ChartWidget/ChartWidgetConfigAdvanced'

// BoardItem i18n strings (required for BoardItem component)
const boardItemI18nStrings = {
  dragHandleAriaLabel: 'Drag handle',
  dragHandleAriaDescription: 'Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to discard.',
  resizeHandleAriaLabel: 'Resize handle',
  resizeHandleAriaDescription: 'Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to discard.',
}

interface ConfigurableWidgetProps {
  widget: DashboardWidget
  onUpdate: (widget: DashboardWidget) => void
  onRemove?: () => void
  isEditing?: boolean
}

export const ConfigurableWidget: React.FC<ConfigurableWidgetProps> = ({
  widget,
  onUpdate,
  onRemove,
  isEditing = false
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [isConfiguring, setIsConfiguring] = useState(false)
  const [legendComponent, setLegendComponent] = useState<React.ReactNode>(null)

  const handleConfigure = () => {
    if (widget.type === 'chart') {
      setIsConfiguring(true)
    }
    // Other widget types will be handled later
  }

  const handleRemove = () => {
    setShowRemoveModal(false)
    if (onRemove) {
      onRemove()
    }
  }

  // Render the widget content based on type
  const renderWidgetContent = () => {
    switch (widget.type) {
      case 'chart':
        return (
          <ChartWidget
            widget={widget}
            onEdit={onUpdate}
            isEditing={isEditing}
            onLegendRender={setLegendComponent}
          />
        )
      
      case 'kpi':
        return (
          <Container>
            <Box textAlign="center" color="text-body-secondary" padding="xl">
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📈</div>
              <div>KPI Widget</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Implementation coming soon...
              </div>
            </Box>
          </Container>
        )
      
      case 'table':
        return (
          <Container>
            <Box textAlign="center" color="text-body-secondary" padding="xl">
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📋</div>
              <div>Table Widget</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Implementation coming soon...
              </div>
            </Box>
          </Container>
        )
      
      case 'alert_status':
        return (
          <Container>
            <Box textAlign="center" color="text-body-secondary" padding="xl">
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
              <div>Alert Status Widget</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Implementation coming soon...
              </div>
            </Box>
          </Container>
        )
      
      default:
        return (
          <Container>
            <Box textAlign="center" color="text-body-secondary" padding="xl">
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>❓</div>
              <div>Unknown Widget Type: {widget.type}</div>
            </Box>
          </Container>
        )
    }
  }

  const getWidgetSettings = () => {
    if (!isEditing) return undefined

    return (
      <ButtonDropdown
        variant="icon"
        ariaLabel="Widget settings"
        items={[
          {
            id: 'configure',
            text: 'Configure',
            iconName: 'settings'
          },
          {
            id: 'duplicate',
            text: 'Duplicate',
            iconName: 'copy'
          },
          {
            id: 'divider-1',
            itemType: 'divider'
          },
          {
            id: 'remove',
            text: 'Remove',
            iconName: 'remove',
            disabled: false
          }
        ]}
        onItemClick={({ detail }) => {
          switch (detail.id) {
            case 'configure':
              handleConfigure()
              break
            case 'duplicate':
              // Duplicate logic will be implemented later
              break
            case 'remove':
              setShowRemoveModal(true)
              break
          }
        }}
      />
    )
  }

  return (
    <>
      <BoardItem
        header={
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '12px',
            width: '100%',
            minHeight: '50px' // Ensure minimum height for legend
          }}>
            {/* Title Section (col-8 equivalent) */}
            <div style={{ 
              flex: '2 1 66%', 
              minWidth: 0 // Allow shrinking
            }}>
              <Header
                variant="h3"
                description={widget.description}
              >
                {widget.title}
                {widget.loading && <Spinner size="normal" />}
              </Header>
            </div>
            
            {/* Legend Section (col-4 equivalent) */}
            {legendComponent && (
              <div style={{ 
                flex: '1 1 50%', // Give more space for horizontal layout
                minWidth: '300px', // Increase minimum width
                maxWidth: '600px', // Allow more width
                height: '50px', // Match header height
                borderLeft: '1px solid #e1e5e9',
                paddingLeft: '12px',
                display: 'flex',
                alignItems: 'stretch'
              }}>
                {legendComponent}
              </div>
            )}
          </div>
        }
        settings={getWidgetSettings()}
        i18nStrings={boardItemI18nStrings}
      >
        {widget.error ? (
          <Alert
            type="error"
            statusIconAriaLabel="Error"
            header="Widget Error"
          >
            {widget.error}
          </Alert>
        ) : (
          renderWidgetContent()
        )}
      </BoardItem>

      {/* Configuration Modal */}
      {isConfiguring && widget.type === 'chart' && (
        <ChartConfigDialog
          widget={widget}
          visible={isConfiguring}
          onDismiss={() => setIsConfiguring(false)}
          onConfirm={(updatedWidget) => {
            onUpdate(updatedWidget)
            setIsConfiguring(false)
          }}
        />
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <Modal
          visible={showRemoveModal}
          onDismiss={() => setShowRemoveModal(false)}
          header="Remove Widget"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button 
                  variant="link" 
                  onClick={() => setShowRemoveModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleRemove}
                >
                  Remove
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Box variant="span">
              Are you sure you want to remove "{widget.title}" from this dashboard?
            </Box>
            <Alert type="warning">
              This action cannot be undone.
            </Alert>
          </SpaceBetween>
        </Modal>
      )}
    </>
  )
}